import React, { useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { FP, fpInputSx } from 'components/financepro';
import DocumentoEmpresarialPreview from 'facturapos/DocumentoEmpresarialPreview';
import { generarHtmlDocumentoEmpresarial, money } from 'facturapos/documentoEmpresarial';

const CONFIGURACION_DEFAULT = {
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: 'Gracias por su preferencia',
  mensajeGarantia: '',
  mensajeDevolucion: '',
  mostrarInfoEmpresa: true,
  nombreEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  rncEmpresa: '',
  sitioWebEmpresa: '',
  mostrarNCF: true,
  mostrarObservaciones: true,
  mostrarDescuentos: true,
  mostrarImpuestos: true,
  mostrarSubtotalAntesDescuentos: true,
  cortarPapelAutomatico: false,
  imprimirLogo: false,
  rutaLogo: '',
  mostrarCodigoBarras: false,
  mostrarQR: false,
  textoQR: '',
};

const btnPrimarySx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
};

const btnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

/**
 * Vista de compra en pantalla: vista previa e impresión en formato empresarial.
 */
const VistaCompra = ({ open, onClose, compraData = {} }) => {
  const { controller, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const nombreEmpresaContexto =
    typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '';
  const direccionEmpresaContexto =
    typeof direccionEmpresaCtx === 'string' &&
    direccionEmpresaCtx.trim() &&
    direccionEmpresaCtx !== 'false'
      ? direccionEmpresaCtx.trim()
      : '';
  const rncEmpresaContexto =
    typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() && rncEmpresaCtx !== 'false'
      ? rncEmpresaCtx.trim()
      : '';

  const printRef = useRef(null);
  const [dialogoEmailAbierto, setDialogoEmailAbierto] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [configuracion, setConfiguracion] = useState(CONFIGURACION_DEFAULT);
  const [cargandoConfig, setCargandoConfig] = useState(false);

  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = new Date().toISOString().split('T')[0],
    fechaVencimiento = '',
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
    cliente_nombre = '',
  } = compraData;

  const totalNotaCredito = compraData.totalNotaCredito ?? compraData.total_notas_credito ?? 0;
  const totalAPagar = Math.max(0, Number(total) - Number(totalNotaCredito));

  useEffect(() => {
    if (!open) return;
    if (compraData.configuracion) {
      const merged = { ...CONFIGURACION_DEFAULT, ...compraData.configuracion };
      if ((compraData.tipo_factura || '') === 'COMPRA') {
        merged.mostrarNCF = false;
      }
      setConfiguracion(merged);
      setCargandoConfig(false);
      return;
    }
    const idEmpresa = compraData.id_empresa;
    const dbEmpresa = idEmpresa && typeof idEmpresa === 'object' ? idEmpresa.db_empresa : null;
    if (!dbEmpresa) {
      setConfiguracion({ ...CONFIGURACION_DEFAULT, ...compraData.configuracion });
      setCargandoConfig(false);
      return;
    }
    setCargandoConfig(true);
    fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id_empresa: idEmpresa,
        usuario: usuarioLog || '',
        password: claveLog || '',
        tipo_documento: compraData.tipo_factura || 'COMPRA',
        tipo_factura: compraData.tipo_factura || 'COMPRA',
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.configuracion) {
          const merged = { ...CONFIGURACION_DEFAULT, ...data.configuracion };
          if ((compraData.tipo_factura || '') === 'COMPRA') {
            merged.mostrarNCF = false;
          }
          setConfiguracion(merged);
        }
      })
      .catch(() => setConfiguracion(CONFIGURACION_DEFAULT))
      .finally(() => setCargandoConfig(false));
  }, [open, compraData.id_empresa, compraData.configuracion, usuarioLog, claveLog, servidor]);

  const configuracionResuelta = {
    ...configuracion,
    nombreEmpresa: nombreEmpresaContexto || configuracion.nombreEmpresa || '',
    direccionEmpresa: direccionEmpresaContexto || configuracion.direccionEmpresa || '',
    rncEmpresa: rncEmpresaContexto || configuracion.rncEmpresa || '',
  };

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  const tituloDocumento =
    tipoDocumento || (documento === 'RECEPCION' ? 'RECEPCIÓN' : 'COMPRA');

  const buildFilasTotales = () => {
    const filas = [{ label: 'Subtotal', value: subtotalAntesDescuentos }];
    if (totalDescuentos > 0) {
      filas.push({ label: 'Descuentos', value: `−${money(totalDescuentos)}`, danger: true });
    }
    filas.push({ label: 'Subtotal neto', value: subtotal });
    if (impuestos.length > 0) {
      impuestos.forEach((imp) => {
        filas.push({ label: imp.nombre || 'Impuesto', value: Number(imp.monto || 0) });
      });
      filas.push({ label: 'Total impuestos', value: totalImpuestos });
    }
    if (totalNotaCredito > 0) {
      filas.push({
        label: 'Nota(s) de crédito',
        value: `−${money(totalNotaCredito)}`,
        danger: true,
      });
    }
    return filas;
  };

  const buildMetaExtra = () => {
    const meta = [];
    if (configuracionResuelta.mostrarNCF !== false && ncf) {
      meta.push({ label: 'NCF', value: ncf });
    }
    return meta;
  };

  const handleImprimir = () => {
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const htmlContent = generarHtmlDocumentoEmpresarial({
      tituloDocumento,
      numero,
      documento,
      fecha,
      fechaVencimiento,
      metaExtra: buildMetaExtra(),
      parte: cliente_nombre ? { titulo: 'Cliente', nombre: cliente_nombre, lineas: [] } : null,
      items,
      filasTotales: buildFilasTotales(),
      totalLabel: 'TOTAL',
      totalValue: totalAPagar,
      observaciones: configuracionResuelta.mostrarObservaciones !== false ? observaciones : '',
      mensajeGracias: configuracionResuelta.mensajeGracias || '',
      leyendaPiePagina: configuracionResuelta.leyendaPiePagina || '',
      nombreEmpresa: configuracionResuelta.nombreEmpresa || 'Empresa',
      direccionEmpresa: configuracionResuelta.direccionEmpresa || '',
      telefonoEmpresa: configuracionResuelta.telefonoEmpresa || empresa?.telefono || '',
      rncEmpresa: configuracionResuelta.rncEmpresa || '',
      emailEmpresa: configuracionResuelta.emailEmpresa || '',
      mostrarInfoEmpresa: configuracionResuelta.mostrarInfoEmpresa !== false,
    });
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  const abrirDialogoEmail = () => setDialogoEmailAbierto(true);
  const cerrarDialogoEmail = () => {
    setDialogoEmailAbierto(false);
    setEmailDestino('');
  };

  const handleEnviarEmail = async () => {
    const email = (emailDestino || '').trim();
    if (!email) {
      showError('Ingrese el correo de destino');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Correo electrónico no válido');
      return;
    }
    setEnviandoEmail(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_enviarfactura_email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: compraData.id_empresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          email_destino: email,
          factura: {
            items,
            impuestos,
            subtotal,
            totalDescuentos,
            totalImpuestos,
            total,
            totalNotaCredito,
            totalAPagar,
            documento,
            numero,
            fecha,
            ncf,
            tipoDocumento,
            observaciones,
            empresa: empresa?.empresa || empresa?.nombre || '',
            cliente_nombre,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess('Documento enviado por correo correctamente');
        cerrarDialogoEmail();
      } else {
        showError(data.Error || 'No se pudo enviar el correo.');
      }
    } catch (e) {
      showError('Error de conexión al enviar el correo');
    } finally {
      setEnviandoEmail(false);
    }
  };

  const filasTotalesPreview = buildFilasTotales();
  const metaExtraPreview = buildMetaExtra();
  const tituloDialogo =
    documento === 'RECEPCION'
      ? `Recepción Nº ${numero || '—'}`
      : `Compra ${documento || 'COMPRA'}-${numero || '—'}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '92vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.navySoft,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}>
              {tituloDialogo}
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
              Vista previa · Formato empresarial
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
          <DocumentoEmpresarialPreview
            printRef={printRef}
            mostrarInfoEmpresa={configuracionResuelta.mostrarInfoEmpresa !== false}
            nombreEmpresa={configuracionResuelta.nombreEmpresa || 'Empresa'}
            direccionEmpresa={configuracionResuelta.direccionEmpresa || ''}
            telefonoEmpresa={configuracionResuelta.telefonoEmpresa || empresa?.telefono || ''}
            rncEmpresa={configuracionResuelta.rncEmpresa || ''}
            emailEmpresa={configuracionResuelta.emailEmpresa || ''}
            tituloDocumento={tituloDocumento}
            numero={numero}
            documento={documento}
            fecha={fecha}
            fechaVencimiento={fechaVencimiento}
            metaExtra={metaExtraPreview}
            parte={cliente_nombre ? { titulo: 'Cliente', nombre: cliente_nombre, lineas: [] } : null}
            items={items}
            filasTotales={filasTotalesPreview}
            totalLabel="TOTAL"
            totalValue={totalAPagar}
            observaciones={
              configuracionResuelta.mostrarObservaciones !== false ? observaciones : ''
            }
            mensajeGracias={configuracionResuelta.mensajeGracias || ''}
            footerNote={configuracionResuelta.leyendaPiePagina || ''}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            variant="outlined"
            disabled={cargandoConfig}
            sx={btnOutlinedSx}
          >
            {cargandoConfig ? 'Cargando formato…' : 'Imprimir'}
          </Button>
          <Button startIcon={<EmailIcon />} onClick={abrirDialogoEmail} variant="outlined" sx={btnOutlinedSx}>
            Enviar por email
          </Button>
          <Button onClick={onClose} variant="contained" sx={btnPrimarySx}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoEmailAbierto}
        onClose={cerrarDialogoEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.soft,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: FP.color.textPrimary }}>Enviar por correo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Correo electrónico"
            type="email"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
            placeholder="ejemplo@correo.com"
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarDialogoEmail} sx={btnOutlinedSx}>
            Cancelar
          </Button>
          <Button onClick={handleEnviarEmail} variant="contained" disabled={enviandoEmail} sx={btnPrimarySx}>
            {enviandoEmail ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VistaCompra;
